import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { AttendanceApi, StudentAttendanceSummaryRow } from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { useDefaultYear } from './useDefaultYear';

const columnHelper = createColumnHelper<StudentAttendanceSummaryRow>();

export function AttendanceSummaryPage() {
  const term = useTerminology();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'rollNumber', defaultSortOrder: 'asc' });
  const { years, yearId, setYearId, effectiveYearId, selectedYear, isLoading: yearLoading, hasAnyData } =
    useDefaultYear();

  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Re-seed the date range from the selected year whenever it changes
  // (including on first load, once the default year resolves).
  useEffect(() => {
    if (selectedYear) {
      setStartDate(selectedYear.startDate.slice(0, 10));
      setEndDate(selectedYear.endDate.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'students-summary', dt.params, effectiveYearId, classId, sectionId, startDate, endDate],
    queryFn: () =>
      AttendanceApi.studentsSummary({
        ...dt.params,
        academicYearId: effectiveYearId || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !yearLoading && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.accessor('admissionNo', { header: 'Admission No' }),
    columnHelper.accessor('rollNumber', { header: 'Roll No', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('studentName', { header: 'Name' }),
    columnHelper.accessor('present', {
      header: 'Present',
      cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge>,
    }),
    columnHelper.accessor('absent', {
      header: 'Absent',
      cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge>,
    }),
    columnHelper.accessor('late', { header: 'Late', cell: (ctx) => <Badge tone="amber">{ctx.getValue()}</Badge> }),
    columnHelper.accessor('holiday', {
      header: 'Holiday',
      cell: (ctx) => <Badge tone="slate">{ctx.getValue()}</Badge>,
    }),
    columnHelper.accessor('halfDay', {
      header: 'Half Day',
      cell: (ctx) => <Badge tone="blue">{ctx.getValue()}</Badge>,
    }),
  ];

  return (
    <>
      <PageHeader
        title="Attendance Summary"
        description={`Present/absent totals per ${term.level.toLowerCase()} student over a date range.`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
        </div>
        <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
          }}
        >
          <option value="">All {term.level}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          disabled={!classId}
        >
          <option value="">All {term.group}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">
          No attendance recorded for the selected filters.
        </div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
          getRowId={(row) => row.studentId}
        />
      )}
    </>
  );
}
